import React from "react";
import { formatCurrency, getFulltimeTableRows, convertNumberToWords } from "../offerJoiningUtils";

export const customFontFamily = {
  bold: "font-bold",
  normal: "font-normal",
  semibold: "font-semibold",
  boldFont: "font-bold",
  normalFont: "",
  italic: "italic",
};

export const config = {
  fontSizeXs: "text-xs",
  fontSizeSm: "text-[11px]",
  fontSizeBase: "text-[12px]",
  fontSizeLg: "text-[12.5px]",
  fontSizeTitle: "text-sm",
  apptRefPrefix: "DYC/HR-APPT/",
};

const FulltimeApptPDF = (props) => {
  const {
    candidateName,
    candidateAddress,
    candidateMobile,
    candidateEmail,
    candidateLocation,
    position,
    offerRefCode,
    apptRefCode,
    offerDateFormatted,
    joiningDateFormatted,
  } = props;

  const fullData = getFulltimeTableRows(props);
  const calc = fullData.totals;
  const ctcYearlyFormatted = formatCurrency(calc.totalY);
  const ctcWords = convertNumberToWords(calc.totalY);

  return (
    <div className={`bg-white text-gray-900 ${config.fontSizeLg} space-y-8`} style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
      {/* PAGE 1 */}
      <div className="space-y-4 border-b pb-8">
        <div className={`flex justify-between items-center ${customFontFamily.bold} text-gray-800 ${config.fontSizeXs} mb-5 pb-1`}>
          <div>{apptRefCode || "DYC/HR-APPT/SKC"}</div>
          <div>Date: {offerDateFormatted}</div>
        </div>

        <div className="space-y-0.5 text-gray-900 pt-2">
          <div>To,</div>
          <div className={`${customFontFamily.bold} ${config.fontSizeTitle}`}>{candidateName},</div>
          {candidateAddress && <div>Address: {candidateAddress}</div>}
          {candidateMobile && <div>Mobile: {candidateMobile}</div>}
          {candidateEmail && <div>Email: {candidateEmail}</div>}
        </div>

        <div className="text-center font-bold text-sm tracking-wide uppercase pt-2">
          APPOINTMENT LETTER
        </div>

        <div className={customFontFamily.bold}>Dear {candidateName.split(" ")[0]},</div>

        <div className="space-y-3 text-justify leading-relaxed">
          <p>
            With reference to your application and subsequent interviews you had with us, we are pleased to appoint you as "<span className={customFontFamily.bold}>{position || "Project Coordinator and Business Development Executive"}</span>" in Grade K1 in our organization with effect from <span className={customFontFamily.bold}>{joiningDateFormatted}</span>, subject to the following terms & conditions. This letter of appointment supersedes all other communications made to yours before the date of issue of this appointment letter.
          </p>

          <div className="pt-2 space-y-1.5">
            <h4 className={`${customFontFamily.bold} text-gray-900`}>1. PLACE OF APPOINTMENT</h4>
            <p>
              Your place of work will be our office at 702, Lodha Supremus, District - II, Kolshet Road, Thane West - 400607. However, you may be required to work anywhere in India, if the company so directs, based on the exigencies of work. You may be posted or transferred to any of the Company's Establishments, Departments, Business Units, or Operations.
            </p>
            <p>
              Your unwavering commitment to dedicating your undivided time and focus to the Company is of paramount importance. Accordingly, you are expressly prohibited from engaging directly or indirectly in any other business, occupation, or employment, whether part-time or full-time, that are either vendors or competitors of the Company.
            </p>
            <p>
              In the interest of transparency and to maintain the integrity of our professional relationship, it is imperative that you disclose any potential conflicts of interest, including those involving your immediate family members, to the Company before formally accepting this appointment. This proactive disclosure is a crucial step in fostering transparency and maintaining the ethical standards expected in our professional collaboration.
            </p>
          </div>

          <div className="pt-2 space-y-1">
            <h4 className={`${customFontFamily.bold} text-gray-900`}>2. REPORTING</h4>
            <p>
              You would be reporting to the Managing Director or to whosoever the company deems fit to be your Manager.
            </p>
          </div>
        </div>
      </div>

      {/* PAGE 2 */}
      <div className="space-y-4 border-b pb-8">
        <h4 className={`${customFontFamily.bold} text-gray-900`}>3. EMPLOYMENT TENURE</h4>
        <h5 className={`${customFontFamily.bold} text-gray-900`}>3.1 PROBATION</h5>
        <p className="text-justify leading-relaxed">
          Upon commencement of your employment with the Company, you will undergo a probationary period lasting for Six Months. The management reserves the right to extend this period based on an evaluation of your conduct, regularity, and performance at work.
        </p>
        <p className="text-justify leading-relaxed">
          Your confirmation in the position will be communicated through a written confirmation letter. Until such confirmation is received, it shall be assumed that you remain under probation.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>3.2 RESIGNATION DURING PROBATION PERIOD</h5>
        <p className="text-justify leading-relaxed">
          In the event of your resignation during the probationary period, you will be relieved within 15 days from the date of submitting your resignation.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>3.3 TERMINATION DURING PROBATIONARY PERIOD</h5>
        <p className="text-justify leading-relaxed">
          During the probationary period, the Company retains the discretion to terminate your employment with immediate effect, without prior notice, and without assigning any specific reason.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>3.4 CONFIRMATION</h5>
        <p className="text-justify leading-relaxed">
          Your confirmation in the position is contingent upon your performance and overall conduct. It is imperative that this confirmation be conveyed in writing. In the absence of a written confirmation, it shall be understood that you remain in a probationary status.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>3.5 RESIGNATION</h5>
        <p className="text-justify leading-relaxed">
          In the event of your resignation, it is imperative to adhere to the Company's prescribed notice period, determined by your Grade at the time of resignation. This procedural requirement is fundamental to ensuring an organized and seamless transition, safeguarding the operational continuity of the Company. Your collaboration in meticulously following this protocol is valued for its contribution to the overall efficiency of the resignation process.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>3.6 TERMINATION DUE TO MISCONDUCT</h5>
        <p className="text-justify leading-relaxed">
          In the unfortunate event of your involvement in activities deemed as misconduct, as assessed by the Company, it grants the Company the authority to terminate your employment abruptly, without the obligation of providing prior notice or compensation in lieu of notice.
        </p>
        <p className="text-justify leading-relaxed">
          Additionally, your employment may face immediate termination without prior notice or compensation if, at any point:
        </p>
        <ul className="list-alpha pl-6 space-y-1">
          <li>a. You violate the terms outlined in this agreement.</li>
          <li>b. You initiate or undergo bankruptcy proceedings.</li>
          <li>c. You encounter a medical condition preventing you from fulfilling your job responsibilities.</li>
          <li>d. A court finds you guilty of a severe offense involving moral turpitude and issues a corresponding sentence.</li>
          <li>e. You engage in actions categorized as grave misconduct, insubordination, or indiscipline.</li>
        </ul>
      </div>

      {/* PAGE 3 */}
      <div className="space-y-4 border-b pb-8">
        <h4 className={`${customFontFamily.bold} text-gray-900`}>3.7 RETIREMENT</h4>
        <p className="text-justify leading-relaxed">
          The designated retirement age is set at 60 years. Upon reaching the age of 60, your retirement will be automatic, and no additional notice will be provided. This policy is aligned with standard retirement practices and ensures a seamless transition into the next phase.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>3.8 SALARY AND BENEFITS DISCLOSURE</h4>
        <p className="text-justify leading-relaxed">
          Your total compensation CTC will be <span className={customFontFamily.bold}>Rs {ctcYearlyFormatted}/- ({ctcWords})</span>, per annum which includes salary, leave salary, bonus, PF, gratuity, any other allowances such as conveyance, travelling, mobile expense and other benefits can be found in the attached <span className={customFontFamily.bold}>CTC ANNEXURE A - Salary Break-up.</span>
        </p>
        <p className="text-justify leading-relaxed">
          It is imperative to note that no additional payments, remunerations, compensations, benefits, or perks, beyond what is explicitly outlined in this agreement or as per the Company's guidelines and policies, shall be extended.
        </p>
        <p className="text-justify leading-relaxed">
          The Annual Salary Review will be contingent upon the Company's overall performance, business outlook, and individual employee contributions throughout the appraisal year. It's essential to mention that employees under probationary status are not eligible for participation in the Annual Salary Review.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>4. RESPONSIBILITIES</h4>
        <p className="text-justify leading-relaxed">
          Detailed job responsibilities will be outlined in your job description, provided to you separately.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>5. TRAINING AND PROFESSIONAL DEVELOPMENT</h4>
        <p className="text-justify leading-relaxed">
          Throughout your tenure with the Company, there may be instances where your participation in training programs or higher education becomes necessary for skill enhancement, business exigencies, or the improvement of client services. In such cases, the Company will bear the associated expenses.
        </p>
        <p className="text-justify leading-relaxed">
          In reciprocation for the provided training or higher education, you will be required to sign an agreement outlining your commitment to serve the Company for a specified duration as outlined in the aforementioned agreement post the completion of the training or education. In the event of a breach of this agreement or discontinuation of your services to the Company for any reason, you will be obligated to reimburse the Company for the incurred training expenses. The specific amounts for such reimbursement will be outlined in the aforementioned agreement.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>6. SERVICE RULES AND COMPLIANCE</h4>
        <p className="text-justify leading-relaxed">
          This appointment is subject to adherence to the comprehensive set of rules, regulations, and policies outlined in the Company's Human Resources (HR) Manual (DYC/HR/01), which may undergo periodic amendments, modifications, or cancellations. The HR Manual encompasses a spectrum of guidelines that necessitate your thorough understanding and strict compliance.
        </p>
      </div>

      {/* PAGE 4 */}
      <div className="space-y-4 border-b pb-8">
        <p className="text-justify leading-relaxed">
          You are obligated to adhere to the service rules applicable based on your designated location, encompassing aspects such as leave entitlement, working hours, Employee Cadre, Statutory Provisions, and Company Policies. Compliance with these rules ensures alignment with the prevailing terms and conditions of employment.
        </p>
        <p className="text-justify leading-relaxed">
          Furthermore, you are expected to abide by any additional rules, regulations, orders, directives and instructions that the Company may introduce over time, governing various facets of employment including but not limited to working conditions, pay and allowance, and more.
        </p>
        <p className="text-justify leading-relaxed">
          For detailed information regarding your leave entitlement, kindly refer to the Leave Rules stipulated in the Company HR Manual. Your commitment to understanding and complying with these regulations is integral to a harmonious and productive professional relationship.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>7. NO SIMULTANEOUS EMPLOYMENT</h4>
        <p className="text-justify leading-relaxed">
          While employed with this company, you will not in any circumstances be permitted to work for any other firm or person, either whole-time or part-time, to own, or in any way be associated with any firm or person as advisor, director or partner, whether paid or not, for your services, without prior written permission of the company.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>8. CONFIDENTIALITY AND NON-DISCLOSURE OBLIGATIONS</h4>
        <p className="text-justify leading-relaxed">
          During your employment and even after it ends, you are bound to keep company information confidential. This includes any formulas, processes, ideas, customer details, and other sensitive information you come across during your work. The Company may request you to sign additional confidentiality or non-disclosure agreements, emphasizing the importance of safeguarding such information.
        </p>
        <p className="text-justify leading-relaxed">
          These obligations remain in force even after your employment concludes, only becoming obsolete for information that becomes public without any fault on your part.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>9. INTELLECTUAL PROPERTY OWNERSHIP</h4>
        <p className="text-justify leading-relaxed">
          Any work-related documents, like designs, drawings, photos, plans, computer programs, or records, created by you during your employment belong to the Company. You agree to give up any intellectual property rights to these documents and promise to sign any necessary papers to transfer these rights. When you leave the Company, you must return all such documents and records.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>10. DATA PROTECTION COMPLIANCE</h4>
        <p className="text-justify leading-relaxed">
          You must follow all the rules about keeping information safe that apply to the Company.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>11. NON-SOLICITATION AGREEMENT</h4>
        <p className="text-justify leading-relaxed">
          You agree not to join or be involved with any company that competes with DYC Global Private Limited for a period of two years after you leave the company. Additionally, you commit not to start your own business that rivals any of the activities of DYC Global Private Limited. During this period, you won't try to persuade other employees to leave their jobs, and you won't attempt to divert business from DYC Global Private Limited by contacting vendors or customers.
        </p>
      </div>

      {/* PAGE 5 */}
      <div className="space-y-4 border-b pb-8">
        <h4 className={`${customFontFamily.bold} text-gray-900`}>12. CUSTODY AND CARE OF COMPANY ASSETS</h4>
        <p className="text-justify leading-relaxed">
          As an employee, it is your responsibility to ensure the safekeeping of any company property entrusted to you. In the event of loss or damage during your possession, you are liable for reimbursement. Upon conclusion of your employment, for any reason, it is imperative to return all company assets in satisfactory condition.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>13. CODE OF CONDUCT, UNDERTAKING AND CONFLICT OF INTEREST</h4>
        <h5 className={`${customFontFamily.bold} text-gray-900`}>13.1 CODE OF CONDUCT</h5>
        <p className="text-justify leading-relaxed">
          You shall follow the rules outlined in our company's HR Manual (DYC/HR/01). This manual defines the expected conduct for all employees, ensuring a positive and respectful work environment.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>13.2 UNDERTAKING</h5>
        <p className="text-justify leading-relaxed">
          You confirm that there are no prior agreements or service conditions from your past employment that would hinder you from joining DYC Global Private Limited.
        </p>
        <p className="text-justify leading-relaxed">
          You assure the accuracy, correctness, and completeness of all details provided in your job application, including supporting documents.
        </p>
        <p className="text-justify leading-relaxed">
          You commit to providing proof of identity, address, date of birth, nomination forms for retirement schemes, as well as relevant certificates and testimonials to support your qualifications and experience.
        </p>
        <p className="text-justify leading-relaxed">
          It is your responsibility to promptly inform us of any changes to this information within one week of such changes occurring.
        </p>

        <h5 className={`${customFontFamily.bold} text-gray-900 pt-2`}>13.3 CONFLICT OF INTEREST</h5>
        <p className="text-justify leading-relaxed">
          In recognition of the critical importance of maintaining transparent professional conduct, You solemnly undertake and guarantee the following as an integral part of your commitment to DYC Global Private Limited:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-justify leading-relaxed">
          <li>You assure that you do not harbour any personal interests that could potentially create a conflict with the interests of the Company.</li>
          <li>You commit to seeking prior approval from DYC Global Private Limited before accepting any directorship or assuming any other position, including roles within non-governmental organizations.</li>
          <li>You acknowledge and accept the responsibility to indemnify and protect DYC Global Private Limited from any and all losses, damages, costs, expenses, or injuries it may suffer or incur due to any breach of the representations, undertakings, and warranties made by you.</li>
        </ul>
        <p className="text-justify leading-relaxed">
          This undertaking signifies my dedication to upholding the highest standards of professional integrity and ensuring the best interests of DYC Global Private Limited are safeguarded at all times.
        </p>
      </div>

      {/* PAGE 6 */}
      <div className="space-y-4 border-b pb-8">
        <h4 className={`${customFontFamily.bold} text-gray-900`}>14. DISCIPLINE</h4>
        <p className="text-justify leading-relaxed">
          a) You will be governed by the company's rules and regulations that may be promulgated from time to time.
        </p>
        <p className="text-justify leading-relaxed">
          b) You shall not refuse to take up any assignment that may be offered to you by the company.
        </p>
        <p className="text-justify leading-relaxed">
          c) You shall be expected to abide by the rules and regulations of the company, be courteous, honest and professional within the company or with its clients/customers, and maintain & represent the Company's high standards of professional Services at all times, whether in the Company or at its client's site(s).
        </p>
        <p className="text-justify leading-relaxed">
          d) You shall be responsible for all company properties and material that are in your possession, and all infrastructure like telephones, computers, projectors etc that have been provided to you to enable you in your work.
        </p>
        <p className="text-justify leading-relaxed">
          e) You shall not publicly or privately criticize, defame or misrepresent the Company and shall not, knowingly, commit any such actions which may result in the Company's image / business being adversely affected.
        </p>
        <p className="text-justify leading-relaxed">
          f) During the course of your employment with the Company, you will not solicit business of any nature, either directly or indirectly, for yourself, or for any other party, from the company's clients and/or customers.
        </p>
        <p className="text-justify leading-relaxed">
          g) Your appointment shall be subject to your abiding by the rules and regulations of the company that are in force from time to time and during the period of your employment with the company.
        </p>
        <p className="text-justify leading-relaxed">
          h) The company shall have right to add, amend and modify any or all of the above terms and conditions of service which shall be binding on you.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>15. POSTAL ADDRESS</h4>
        <p className="text-justify leading-relaxed">
          You will keep the company informed of the postal address along with contact number for communication, including the changes that may occur during your period of employment with the company.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>16. WORKING HOURS</h4>
        <p className="text-justify leading-relaxed">
          You are expected to work & complete your 8.30 hours daily, between 9:30 AM to 6:00 PM daily. The working hours are subject to change, depending on the requirements of the Company. There will be weekly holidays on every Sunday. However, if required, you need to work on Sunday too and extra compensation will be paid for the same.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>17. OWNERSHIP OF INVENTIONS, IDEAS, ETC.</h4>
        <p className="text-justify leading-relaxed">
          a) The company shall be the sole owner of all inventions, ideas, materials, systems, codes, etc. that may be devised / created by the Employee in the course of his/ her employment with the company.
        </p>
      </div>

      {/* PAGE 7 */}
      <div className="space-y-4 border-b pb-8">
        <p className="text-justify leading-relaxed">
          b) The employee shall have no claim of any nature whatsoever and undertakes not to utilize, part with or divulge such information to any third party, even after he / she has ceased to be an employee of the company.
        </p>
        <p className="text-justify leading-relaxed">
          c) The above terms and conditions shall be binding upon both the parties hereto during the entire course of employment of the Employee with the company.
        </p>
        <p className="text-justify leading-relaxed">
          d) Apart from the above terms and conditions, you shall be bound by, and shall adhere to such other rules and regulations as may be in force, from time to time, in the Company.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>18. AUTHENTICITY OF INFORMATION</h4>
        <p className="text-justify leading-relaxed">
          This appointment is made on the basis of information provided by you in the application and also at the time of interview. Original copies of your certificates, testimonials and previous employment records should be submitted along with the photocopies for verification. If any declarations given by you to the Company prove to be false or if you are found to have wilfully suppressed any material information, you will be liable to be removed from service without any notice.
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>19. STATUTORY BENEFITS AND DEDUCTIONS</h4>
        <p className="text-justify leading-relaxed">
          All payments made under this agreement are subject to deductions for taxes as per the prevailing rates applicable at the relevant time.
        </p>
        <p className="text-justify leading-relaxed">
          This appointment letter is also governed by the various prevailing rules, regulations and the HR Manual (DYC/HR/01) of the Company as amended, modified or cancelled from time to time.
        </p>
        <p className="text-justify leading-relaxed">
          The Company's HR Manual contains various rules and regulations which you are required to familiarize and ensure compliance. You shall be governed by service rules applicable as per location of posting including but not limited to, leave entitlement, working hours, Employee Cadre, Statutory Provisions and/or Company Policies in force from time to time as the case may be. You will also abide by such rules, regulations, orders, directives and instructions as the Company may frame from time to time governing the terms of employment, conditions of service, working conditions, pay and allowance, Quality Health Safety & Environment (QHSE) etc.
        </p>
        <p className="text-justify leading-relaxed">
          Your leave entitlement will be governed by the prevailing Leave Rules. The details are available in the Company HR Manual (DYC/HR/01).
        </p>

        <h4 className={`${customFontFamily.bold} text-gray-900 pt-2`}>20. JURISDICTION CLAUSES</h4>
        <p className="text-justify leading-relaxed">
          In matters concerning this appointment, the governing authority is the Head Office of DYC Global Private Limited, situated in Thane Maharashtra.
        </p>
        <p className="text-justify leading-relaxed">
          In the event of any dispute or differences related to this contract or its termination, jurisdiction will lie with the courts and relevant authorities in Thane Maharashtra.
        </p>
        <p className="text-justify leading-relaxed font-semibold">
          Your confirmation and acceptance of the outlined terms and conditions are kindly requested by signing and returning the enclosed duplicate copy of this appointment letter.
        </p>
      </div>

      {/* PAGE 8 - Signatures */}
      <div className="space-y-8 border-b pb-8">
        <p>We wish you every success in your career with us.</p>

        <div className="space-y-2 pt-2">
          <div>Yours faithfully,</div>
          <div className="my-3">
            <img src="/img/contract_stamp.png" alt="Company Stamp" className="h-32 object-contain" />
          </div>
        </div>

        <div className="pt-8 space-y-3">
          <h4 className={`${customFontFamily.bold} text-base text-gray-900`}>Acknowledgement</h4>
          <div className={customFontFamily.bold}>The above terms and conditions are acceptable to me</div>
          <div className="pt-4">Signature: __________________________</div>
          <div>Name: <span className={customFontFamily.bold}>{String(candidateName || "").toUpperCase()}</span></div>
          <div>Date: __________________________</div>
        </div>
      </div>

      {/* PAGE 9 - CTC Annexure A */}
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-2 text-xs border-b pb-3">
          <div><span className={customFontFamily.bold}>Name:</span> {candidateName}</div>
          <div><span className={customFontFamily.bold}>Location:</span> {candidateLocation || "Thane"}</div>
          <div><span className={customFontFamily.bold}>Designation:</span> {position}</div>
          <div><span className={customFontFamily.bold}>Department:</span> Inspection / Business Development</div>
          <div><span className={customFontFamily.bold}>Grade:</span> K1</div>
        </div>

        <h3 className="text-center font-bold text-sm tracking-wide">
          CTC Annexure - Salary Break-up
        </h3>

        <table className={`w-full border-collapse border border-gray-400 ${config.fontSizeXs}`}>
          <thead>
            <tr className={`bg-gray-100 ${customFontFamily.bold} border-b border-gray-400 text-gray-900`}>
              <th className="border border-gray-400 px-3 py-1.5 text-left w-1/2">Particulars</th>
              <th className="border border-gray-400 px-3 py-1.5 text-right">Monthly</th>
              <th className="border border-gray-400 px-3 py-1.5 text-right">Yearly</th>
            </tr>
          </thead>
          <tbody>
            {fullData.subA.map((r, idx) => (
              <tr key={idx}>
                <td className="border border-gray-400 px-3 py-1">{r.label}</td>
                <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(r.monthly)}</td>
                <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(r.yearly)}</td>
              </tr>
            ))}
            <tr className={customFontFamily.bold}>
              <td className="border border-gray-400 px-3 py-1">Sub Total (A)</td>
              <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(calc.subAM)}</td>
              <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(calc.subAY)}</td>
            </tr>
            {fullData.subB.map((r, idx) => (
              <tr key={idx}>
                <td className="border border-gray-400 px-3 py-1">{r.label}</td>
                <td className="border border-gray-400 px-3 py-1 text-right">{r.monthlyLabel || (r.monthly ? formatCurrency(r.monthly) : "")}</td>
                <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(r.yearly)}</td>
              </tr>
            ))}
            <tr className={customFontFamily.bold}>
              <td className="border border-gray-400 px-3 py-1">Sub Total B</td>
              <td className="border border-gray-400 px-3 py-1 text-right"></td>
              <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(calc.subBY)}</td>
            </tr>
            <tr className={`${customFontFamily.bold} bg-gray-50`}>
              <td className="border border-gray-400 px-3 py-1">Total (A+B)</td>
              <td className="border border-gray-400 px-3 py-1 text-right"></td>
              <td className="border border-gray-400 px-3 py-1 text-right">{formatCurrency(calc.totalY)}</td>
            </tr>
          </tbody>
        </table>

        <div className="text-xs text-gray-700 italic">
          Remark : Notes mentioned in offer No. <span className={customFontFamily.bold}>{offerRefCode || "DYC/HR-OFFER-PR/001"}</span> shall be applicable.
        </div>

        <div className="flex justify-between items-start pt-6">
          <div>
            <img src="/img/contract_stamp.png" alt="Company Stamp" className="h-28 object-contain" />
          </div>

          <div className="text-right space-y-2">
            <div className={customFontFamily.bold}>Accepted & Confirmed Above</div>
            <div className="pt-4">Signature: __________________________</div>
            <div>Name: <span className={customFontFamily.bold}>{String(candidateName || "").toUpperCase()}</span></div>
            <div>Date: __________________________</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FulltimeApptPDF;
